import { useEffect, useState, type FormEvent } from 'react';
import {
  CALENDAR_EVENT_TITLE_MAX,
  ENGAGEMENT_CHECKLIST_TEMPLATE,
  ENGAGEMENT_TYPE_LABEL,
  MEETING_REPEAT_LABEL,
  MEETING_RESPONSE_LABEL,
  MEETING_STATUS_LABEL,
  MeetingRepeat,
  MeetingStatus,
  type CalendarEventKind,
  type CalendarEventSummary,
  type EngagementChecklistItem,
  type EngagementType,
  type MeetingConflict,
  type ValidationDetails,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { calendarApi } from '../api/calendar.api';
import { MeetingNotes } from './MeetingNotes';
import { MeetingBriefingPanel } from './MeetingBriefing';
import { AttendeePicker } from './AttendeePicker';
import { StakeholderSelect } from './StakeholderSelect';
import { useAuth } from '../hooks/useAuth';
import { STAKEHOLDER_TAG_LABEL, type EventGuest, type MeetingOrgOptions } from '@todo/shared';

/**
 * Create or edit a calendar event - a one-off meeting or reminder, not a
 * task. Owner-only: CalendarPage never mounts this for an event the caller
 * merely attends, since there is nothing here for them to change.
 */

interface CalendarEventFormProps {
  /** Present when editing; absent when creating. */
  initial?: CalendarEventSummary;
  /** Pre-fills the date when creating from a clicked day cell. */
  defaultDate?: string;
  /** Pre-fills the start time (HH:mm) when creating from a clicked week
   *  slot; the end defaults to 30 minutes later. */
  defaultTime?: string;
  onSaved: (event: CalendarEventSummary) => void;
  onDeleted?: (id: string) => void;
  onCancel: () => void;
  /**
   * A checklist write already saved server-side. Distinct from onSaved,
   * which means "the form was submitted": this fires without closing the
   * panel, so the calendar behind it can pick up the new progress.
   */
  onEventUpdated?: (event: CalendarEventSummary) => void;
}

const REMIND_OPTIONS = [
  { value: '', label: 'No reminder' },
  { value: '0', label: 'At the start time' },
  { value: '5', label: '5 minutes before' },
  { value: '15', label: '15 minutes before' },
  { value: '30', label: '30 minutes before' },
  { value: '60', label: '1 hour before' },
  { value: '1440', label: '1 day before' },
] as const;

/** Local YYYY-MM-DD, the shape <input type="date"> uses. */
function todayLocalDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function localTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function localDate(iso: string): string {
  const d = new Date(iso);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Minutes between an event's start and its reminder, or '' if there is none. */
function remindOptionFor(event: CalendarEventSummary): string {
  if (!event.remindAt) return '';
  const minutes = Math.round(
    (new Date(event.startsAt).getTime() - new Date(event.remindAt).getTime()) / 60_000,
  );
  return REMIND_OPTIONS.some((o) => o.value === String(minutes)) ? String(minutes) : '';
}

export function CalendarEventForm({
  initial, defaultDate, defaultTime, onSaved, onDeleted, onCancel, onEventUpdated,
}: CalendarEventFormProps) {
  /** "HH:mm" plus 30 minutes, staying inside the same day. */
  const halfHourAfter = (time: string): string => {
    const [h, m] = time.split(':').map(Number);
    const total = Math.min(h * 60 + m + 30, 23 * 60 + 59);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [date, setDate] = useState(initial ? localDate(initial.startsAt) : defaultDate ?? todayLocalDate());
  const [startTime, setStartTime] = useState(initial ? localTime(initial.startsAt) : defaultTime ?? '09:00');
  const [endTime, setEndTime] = useState(initial ? localTime(initial.endsAt) : halfHourAfter(defaultTime ?? '09:00'));
  const [remindOption, setRemindOption] = useState(initial ? remindOptionFor(initial) : '15');
  const [status, setStatus] = useState<MeetingStatus>(initial?.status ?? 'CONFIRMED');
  /** Create-only: a repeating meeting materialises its occurrences at save
   *  time, so the rule cannot be changed afterwards. */
  const [repeat, setRepeat] = useState<MeetingRepeat>('NONE');
  /**
   * External guests from the Stakeholders module. Separate state from
   * attendees on purpose: guests never RSVP and are never notified, so
   * merging the two lists would promise something the API cannot keep.
   */
  const { memberships } = useAuth();

  /*
   * A FAILED CALL IS NOT "you have one organisation".
   *
   * Swallowing the error left the dropdown hidden and the two states
   * indistinguishable: a single-org user and a user whose API has not
   * been redeployed both saw exactly nothing. The flag below lets the
   * form say which, instead of silently dropping a feature.
   */
  const [orgsFailed, setOrgsFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    calendarApi.myOrgs()
      .then((list) => { if (!cancelled) { setMyOrgs(list); setOrgsFailed(false); } })
      .catch(() => { if (!cancelled) setOrgsFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const [guests, setGuests] = useState<EventGuest[]>(initial?.guests ?? []);
  /**
   * Which recipient groups built this room - PROVENANCE, sent so the
   * calendar can be filtered by group later. It never decides who is
   * invited; `attendeeIds` does, and removing somebody by hand afterwards
   * does not retract the record that the group was used.
   */
  const [groupIds, setGroupIds] = useState<string[]>(initial?.recipientGroupIds ?? []);

  /*
   * WHICH ORGANISATION this meeting belongs to.
   *
   * A person in several orgs books into any of them without switching.
   * On EDIT the org is fixed - the meeting already belongs to one, its
   * attendees are its people, and moving it would mean rebuilding the
   * whole room - so the dropdown only appears when creating.
   */
  const [orgId, setOrgId] = useState<string>(
    initial?.orgId ?? memberships.find((m) => m.isCurrent)?.orgId ?? '',
  );
  const [myOrgs, setMyOrgs] = useState<Array<{ orgId: string; orgName: string }>>([]);
  const [orgOptions, setOrgOptions] = useState<MeetingOrgOptions | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  /*
   * Everything the three pickers show, refetched when the organisation
   * changes - and the selections CLEARED with it. Keeping an attendee
   * from the previous org would produce a meeting whose invitee is not in
   * its own organisation, which the API now rejects; clearing is both
   * honest and the only thing that could be meant.
   */
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setOptionsLoading(true);
    calendarApi.orgOptions(orgId)
      .then((opts) => { if (!cancelled) setOrgOptions(opts); })
      .catch(() => { if (!cancelled) setOrgOptions(null); })
      .finally(() => { if (!cancelled) setOptionsLoading(false); });
    return () => { cancelled = true; };
  }, [orgId]);

  const [attendeeIds, setAttendeeIds] = useState<string[]>(
    initial?.attendees.map((a) => a.userId) ?? [],
  );
  /** MEETING or ENGAGEMENT. Fixed once saved - a rally does not become a
   *  one-to-one, and switching would orphan the prep checklist. */
  const [kind, setKind] = useState<CalendarEventKind>(initial?.kind ?? 'MEETING');
  const [engagementType, setEngagementType] = useState<EngagementType>(
    initial?.engagementType ?? 'RALLY',
  );
  const [expectedAttendance, setExpectedAttendance] = useState(
    initial?.expectedAttendance != null ? String(initial.expectedAttendance) : '',
  );
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /** Who is already booked at this time - advisory, never blocks saving. */
  const [conflicts, setConflicts] = useState<MeetingConflict[]>([]);
  /**
   * The prep checklist, edited IN PLACE. Checklist writes are their own
   * endpoints - they do not wait for Save changes, because ticking "permit
   * obtained" is a fact about the world, not a draft edit to be discarded
   * if the panel is cancelled.
   */
  const [checklist, setChecklist] = useState<EngagementChecklistItem[]>(
    initial?.checklist ?? [],
  );
  const [newStep, setNewStep] = useState('');
  const [checklistBusy, setChecklistBusy] = useState(false);

  // Re-seed when switching from one event to another (or to "new") without
  // this panel unmounting.
  useEffect(() => {
    setTitle(initial?.title ?? '');
    setDescription(initial?.description ?? '');
    setLocation(initial?.location ?? '');
    setDate(initial ? localDate(initial.startsAt) : defaultDate ?? todayLocalDate());
    setStartTime(initial ? localTime(initial.startsAt) : defaultTime ?? '09:00');
    setEndTime(initial ? localTime(initial.endsAt) : halfHourAfter(defaultTime ?? '09:00'));
    setRemindOption(initial ? remindOptionFor(initial) : '15');
    setStatus(initial?.status ?? 'CONFIRMED');
    setRepeat('NONE');
    setAttendeeIds(initial?.attendees.map((a) => a.userId) ?? []);
    setKind(initial?.kind ?? 'MEETING');
    setEngagementType(initial?.engagementType ?? 'RALLY');
    setExpectedAttendance(
      initial?.expectedAttendance != null ? String(initial.expectedAttendance) : '',
    );
    setChecklist(initial?.checklist ?? []);
    setNewStep('');
    setErrors({});
    setFormError(null);
    setConfirmDelete(false);
    setConflicts([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, defaultDate, defaultTime]);

  /**
   * Live clash check while the times and the guest list are still being
   * chosen - the organiser plus every invitee. Debounced, because it fires
   * on each keystroke in a time field; a stale answer is discarded rather
   * than raced, so the last edit always wins. A failure is silent: this is
   * advice, and advice that cannot be fetched must not break the form.
   */
  useEffect(() => {
    const startsAt = new Date(`${date}T${startTime}`);
    const endsAt = new Date(`${date}T${endTime}`);
    if (Number.isNaN(startsAt.getTime()) || endsAt <= startsAt) {
      setConflicts([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      void calendarApi
        .conflicts({
          startsAt: startsAt.toISOString(),
          endsAt: endsAt.toISOString(),
          attendeeIds,
          ...(initial ? { excludeEventId: initial.id } : {}),
        })
        .then((found) => { if (!cancelled) setConflicts(found); })
        .catch(() => { if (!cancelled) setConflicts([]); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [date, startTime, endTime, attendeeIds, initial]);

  /** One checklist write, then keep the panel and the calendar in step. */
  async function runChecklist(action: () => Promise<CalendarEventSummary>) {
    setChecklistBusy(true);
    try {
      const updated = await action();
      setChecklist(updated.checklist);
      onEventUpdated?.(updated);
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : 'That checklist change did not save.',
      );
    } finally {
      setChecklistBusy(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);

    if (!title.trim()) {
      setErrors({ title: 'Give the event a title' });
      return;
    }

    const startsAt = new Date(`${date}T${startTime}`);
    const endsAt = new Date(`${date}T${endTime}`);
    if (endsAt <= startsAt) {
      setErrors({ endTime: 'End time must be after the start time' });
      return;
    }

    // Marking a meeting RESCHEDULED requires the alternate slot: the date
    // or times must actually change (the API enforces the same rule).
    if (
      initial &&
      status === 'RESCHEDULED' &&
      initial.status !== 'RESCHEDULED' &&
      startsAt.getTime() === new Date(initial.startsAt).getTime() &&
      endsAt.getTime() === new Date(initial.endsAt).getTime()
    ) {
      setErrors({ date: 'Pick the alternate date and time for the rescheduled meeting' });
      return;
    }

    const remindAt = remindOption
      ? new Date(startsAt.getTime() - Number(remindOption) * 60_000).toISOString()
      : null;

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        remindAt,
        status,
        attendeeIds,
        // Only an Admin can even see the picker; the API drops these for
        // anyone else, so sending them is harmless either way.
        ...(orgOptions?.canPickGuests
          ? { guestIds: guests.map((g) => g.stakeholderId) }
          : {}),
        /* Only on create: an existing meeting's organisation is fixed. */
        ...(initial ? {} : { orgId }),
        ...(groupIds.length > 0 ? { recipientGroupIds: groupIds } : {}),
        ...(kind === 'ENGAGEMENT'
          ? {
              engagementType,
              expectedAttendance: expectedAttendance.trim()
                ? Number(expectedAttendance)
                : null,
            }
          : {}),
      };

      let saved: CalendarEventSummary;
      if (initial) {
        // PATCH manners: send only the fields that actually changed. A save
        // with nothing changed does not even hit the network.
        const sameInstant = (a: string | null, b: string | null) =>
          (a ? new Date(a).getTime() : null) === (b ? new Date(b).getTime() : null);
        const beforeAttendees = initial.attendees.map((a) => a.userId);
        const sameAttendees =
          attendeeIds.length === beforeAttendees.length &&
          attendeeIds.every((id) => beforeAttendees.includes(id));

        const changes: Partial<typeof payload> = {};
        if (payload.title !== initial.title) changes.title = payload.title;
        if ((payload.description ?? '') !== (initial.description ?? '')) changes.description = payload.description;
        if ((payload.location ?? '') !== (initial.location ?? '')) changes.location = payload.location;
        if (!sameInstant(payload.startsAt, initial.startsAt)) changes.startsAt = payload.startsAt;
        if (!sameInstant(payload.endsAt, initial.endsAt)) changes.endsAt = payload.endsAt;
        if (!sameInstant(payload.remindAt, initial.remindAt)) changes.remindAt = payload.remindAt;
        if (payload.status !== initial.status) changes.status = payload.status;
        if (!sameAttendees) changes.attendeeIds = payload.attendeeIds;
        const beforeGuests = (initial.guests ?? []).map((g) => g.stakeholderId);
        const nowGuests = guests.map((g) => g.stakeholderId);
        const sameGuests =
          nowGuests.length === beforeGuests.length &&
          nowGuests.every((id) => beforeGuests.includes(id));
        if (orgOptions?.canPickGuests && !sameGuests) changes.guestIds = nowGuests;
        const beforeGroups = initial.recipientGroupIds ?? [];
        const sameGroups =
          groupIds.length === beforeGroups.length &&
          groupIds.every((id) => beforeGroups.includes(id));
        if (!sameGroups) changes.recipientGroupIds = groupIds;
        if (initial.kind === 'ENGAGEMENT') {
          if (engagementType !== initial.engagementType) changes.engagementType = engagementType;
          const before = initial.expectedAttendance;
          const now = expectedAttendance.trim() ? Number(expectedAttendance) : null;
          if (now !== before) changes.expectedAttendance = now;
        }

        saved = Object.keys(changes).length === 0
          ? initial
          : await calendarApi.update(initial.id, changes);
      } else {
        saved = await calendarApi.create({ ...payload, repeat, kind });
      }
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_ERROR') {
        const details = err.details as ValidationDetails | undefined;
        const flat: Record<string, string> = {};
        for (const [field, messages] of Object.entries(details ?? {})) {
          const key = field.replace(/^body\./, '');
          flat[key] = messages[0] ?? 'Invalid value';
        }
        setErrors(flat);
      } else {
        setFormError(err instanceof ApiError ? err.message : 'Could not save the event');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!initial) return;
    setSaving(true);
    try {
      await calendarApi.remove(initial.id);
      onDeleted?.(initial.id);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not delete the event');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex h-full flex-col">
      <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
        {formError && (
          <div role="alert" className="rounded-lg bg-red-50 px-3.5 py-2.5 text-sm text-red-700 ring-1 ring-red-100">
            {formError}
          </div>
        )}

        {/* What is being scheduled. Only offered while creating: an
            existing rally cannot become a meeting without stranding its
            prep checklist, so the choice is shown read-only afterwards. */}
        {!initial ? (
          <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-1">
            {([['MEETING', 'Meeting'], ['ENGAGEMENT', 'Public Engagement']] as const).map(
              ([value, label]) => (
                <button key={value} type="button" onClick={() => setKind(value)}
                  className={[
                    'flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition',
                    kind === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
                  ].join(' ')}>
                  {label}
                </button>
              ),
            )}
          </div>
        ) : initial.kind === 'ENGAGEMENT' ? (
          <p className="rounded-lg bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700">
            Public Engagement · {ENGAGEMENT_TYPE_LABEL[initial.engagementType ?? 'OTHER']}
          </p>
        ) : null}

        {kind === 'ENGAGEMENT' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="engagement-type" className="block text-sm font-medium text-slate-700">
                Engagement type <span className="ml-1 text-red-500">*</span>
              </label>
              <select id="engagement-type" value={engagementType} disabled={saving}
                onChange={(e) => setEngagementType(e.target.value as EngagementType)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500">
                {(Object.keys(ENGAGEMENT_TYPE_LABEL) as EngagementType[]).map((t) => (
                  <option key={t} value={t}>{ENGAGEMENT_TYPE_LABEL[t]}</option>
                ))}
              </select>
              {!initial && (
                <p className="mt-1 text-xs text-slate-500">
                  Seeds a {ENGAGEMENT_CHECKLIST_TEMPLATE[engagementType].length}-step prep
                  checklist you can edit afterwards.
                </p>
              )}
            </div>
            <div>
              <label htmlFor="engagement-turnout" className="block text-sm font-medium text-slate-700">
                Expected turnout <span className="ml-1 text-slate-400">(optional)</span>
              </label>
              <input id="engagement-turnout" type="number" min={0} inputMode="numeric"
                value={expectedAttendance} disabled={saving}
                onChange={(e) => setExpectedAttendance(e.target.value)}
                placeholder="e.g. 500"
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500" />
            </div>
          </div>
        )}

        {/* Prep checklist - only on a SAVED engagement: the template is
            seeded server-side at creation, so there is nothing to show
            until the event exists. Saves immediately, not on submit. */}
        {initial?.kind === 'ENGAGEMENT' && (
          <div className="rounded-lg border border-violet-200 bg-violet-50/50 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-violet-700">
                Prep checklist
              </p>
              <span className="text-xs font-semibold text-violet-700">
                {checklist.filter((c) => c.done).length}/{checklist.length}
              </span>
            </div>

            {checklist.length > 0 && (
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-violet-200">
                <div className="h-full rounded-full bg-violet-600 transition-all"
                  style={{
                    width: `${(checklist.filter((c) => c.done).length / checklist.length) * 100}%`,
                  }} />
              </div>
            )}

            <ul className="mt-2 space-y-1.5">
              {checklist.map((item) => (
                <li key={item.id} className="group flex items-start gap-2">
                  <input type="checkbox" checked={item.done} disabled={checklistBusy}
                    onChange={(e) => void runChecklist(
                      () => calendarApi.updateChecklistItem(initial.id, item.id, {
                        done: e.target.checked,
                      }),
                    )}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-violet-600 focus:ring-violet-500 disabled:opacity-50" />
                  <span className={[
                    'min-w-0 flex-1 text-sm',
                    item.done ? 'text-slate-400 line-through' : 'text-slate-700',
                  ].join(' ')}>
                    {item.label}
                  </span>
                  <button type="button" disabled={checklistBusy}
                    aria-label={`Remove ${item.label}`}
                    onClick={() => void runChecklist(
                      () => calendarApi.removeChecklistItem(initial.id, item.id),
                    )}
                    className="shrink-0 px-1 text-slate-300 opacity-0 transition hover:text-red-500 group-hover:opacity-100 disabled:opacity-30">
                    &times;
                  </button>
                </li>
              ))}
              {checklist.length === 0 && (
                <li className="text-xs text-slate-400">
                  Nothing on the list - add the first step below.
                </li>
              )}
            </ul>

            <div className="mt-2 flex gap-1.5">
              <input value={newStep} disabled={checklistBusy}
                onChange={(e) => setNewStep(e.target.value)}
                onKeyDown={(e) => {
                  // Enter adds the step; it must NOT submit the whole form.
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  const label = newStep.trim();
                  if (!label) return;
                  setNewStep('');
                  void runChecklist(() => calendarApi.addChecklistItem(initial.id, label));
                }}
                placeholder="Add a step"
                className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm outline-none transition focus:border-violet-500" />
              <button type="button" disabled={checklistBusy || !newStep.trim()}
                onClick={() => {
                  const label = newStep.trim();
                  if (!label) return;
                  setNewStep('');
                  void runChecklist(() => calendarApi.addChecklistItem(initial.id, label));
                }}
                className="shrink-0 rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50">
                Add
              </button>
            </div>

            <p className="mt-1.5 text-xs text-slate-500">
              Checklist changes save straight away - Save changes is only for the fields above.
            </p>
          </div>
        )}

        {/* Briefing first, notepad second: you prepare before, you record
            after, and the panel reads in that order. */}
        {initial && <MeetingBriefingPanel event={initial} />}

        {/* The notepad and the follow-up draft. Only on a SAVED event -
            there is nothing to attach notes to before that - and, like the
            checklist, it writes on its own endpoint rather than through
            this form's Save. */}
        {initial && (
          <MeetingNotes
            event={initial}
            canEdit={initial.isOwner}
            onSaved={(updated) => onEventUpdated?.(updated)}
          />
        )}

        <div>
          <label htmlFor="event-title" className="block text-sm font-medium text-slate-700">
            Title <span className="ml-1 text-red-500">*</span>
          </label>
          <input
            id="event-title"
            value={title}
            maxLength={CALENDAR_EVENT_TITLE_MAX}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Daily stand up"
            aria-invalid={errors.title ? true : undefined}
            className={[
              'mt-1.5 w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:ring-2',
              errors.title
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
            ].join(' ')}
          />
          {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
        </div>

        <div>
          <label htmlFor="event-date" className="block text-sm font-medium text-slate-700">
            Date <span className="ml-1 text-red-500">*</span>
          </label>
          <input
            id="event-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            aria-invalid={errors.date ? true : undefined}
            className={[
              'mt-1.5 w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:ring-2',
              errors.date
                ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
            ].join(' ')}
          />
          {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="event-start" className="block text-sm font-medium text-slate-700">
              Start time
            </label>
            <input
              id="event-start"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>
          <div>
            <label htmlFor="event-end" className="block text-sm font-medium text-slate-700">
              End time
            </label>
            <input
              id="event-end"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              aria-invalid={errors.endTime ? true : undefined}
              className={[
                'mt-1.5 w-full rounded-lg border px-3 py-2 text-sm outline-none transition focus:ring-2',
                errors.endTime
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                  : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
              ].join(' ')}
            />
            {errors.endTime && <p className="mt-1 text-xs text-red-600">{errors.endTime}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="event-location" className="block text-sm font-medium text-slate-700">
            Location <span className="ml-1 text-slate-400">(optional)</span>
          </label>
          <input
            id="event-location"
            value={location}
            maxLength={200}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Add a room or a link"
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </div>

        <div>
          <label htmlFor="event-remind" className="block text-sm font-medium text-slate-700">
            Reminder
          </label>
          <select
            id="event-remind"
            value={remindOption}
            onChange={(e) => setRemindOption(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          >
            {REMIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            You and everyone invited get a notification at this time.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="event-status" className="block text-sm font-medium text-slate-700">
              Status
            </label>
            <select
              id="event-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as MeetingStatus)}
              className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              {Object.values(MeetingStatus)
                /* Creating: only Tentative / Confirmed - a brand-new meeting
                   cannot be cancelled or rescheduled; those two appear once
                   the meeting exists (editing shows all four). */
                .filter((value) => initial || value === 'TENTATIVE' || value === 'CONFIRMED')
                .map((value) => (
                  <option key={value} value={value}>{MEETING_STATUS_LABEL[value]}</option>
                ))}
            </select>
            {initial && status === 'RESCHEDULED' && initial.status !== 'RESCHEDULED' && (
              <p className="mt-1 text-xs text-amber-600">
                Set the alternate date and time in the fields above - the
                meeting moves to that new slot.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="event-repeat" className="block text-sm font-medium text-slate-700">
              Repeat
            </label>
            {initial ? (
              /* Create-only: the occurrences already exist as real events. */
              <p className="mt-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                {initial.repeat === 'NONE'
                  ? 'Does not repeat'
                  : `${MEETING_REPEAT_LABEL[initial.repeat]} (part of a series)`}
              </p>
            ) : (
              <select
                id="event-repeat"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value as MeetingRepeat)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              >
                {Object.values(MeetingRepeat).map((value) => (
                  <option key={value} value={value}>{MEETING_REPEAT_LABEL[value]}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        {!initial && repeat !== 'NONE' && (
          <p className="-mt-3 text-xs text-slate-500">
            Creates the next {repeat === 'DAILY' ? '30 daily' : repeat === 'WEEKLY' ? '12 weekly' : '12 monthly'} occurrences
            as separate events - each can be edited or cancelled on its own.
          </p>
        )}

        {/* Who has answered - the organiser's whole reason for inviting.
            Read from the SAVED event, not the picker: someone just added
            and not yet saved has nothing to answer. */}
        {kind === 'ENGAGEMENT' && (
          <p className="-mb-2 text-sm font-medium text-slate-700">
            Staff on this engagement{' '}
            <span className="font-normal text-slate-400">
              (they can see it; nobody is invited and there is nothing to accept)
            </span>
          </p>
        )}

        {kind === 'MEETING' && initial && initial.attendees.length > 0 && (
          <div className="rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-slate-200">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Responses
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {(() => {
                const by = (r: string) => initial.attendees.filter((a) => a.response === r).length;
                return `Accepted ${by('ACCEPTED')} · Declined ${by('DECLINED')} · Not sure ${by('TENTATIVE')} · No answer ${by('PENDING')}`;
              })()}
            </p>
            <ul className="mt-2 space-y-1">
              {initial.attendees.map((a) => (
                <li key={a.userId} className="flex items-center justify-between gap-2 text-sm text-slate-700">
                  <span className="truncate">{a.displayName}</span>
                  <span className={[
                    'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                    a.response === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-800'
                      : a.response === 'DECLINED' ? 'bg-red-100 text-red-700'
                      : a.response === 'TENTATIVE' ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-200 text-slate-600',
                  ].join(' ')}>
                    {MEETING_RESPONSE_LABEL[a.response]}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Only when there is a choice to make. One organisation means no
            dropdown at all - a select with a single option is furniture. */}
        {!initial && orgsFailed && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-100">
            Could not load your organisations, so this meeting will be created
            in the one you are signed into. If you belong to more than one,
            the API may need redeploying.
          </p>
        )}

        {!initial && myOrgs.length > 1 && (
          <label className="block">
            <span className="block text-sm font-medium text-slate-700">Organisation</span>
            <select
              value={orgId}
              disabled={saving}
              onChange={(e) => {
                setOrgId(e.target.value);
                /* The people, guests and groups below all belong to the
                   old org. Carrying any of them across would be invalid. */
                setAttendeeIds([]);
                setGuests([]);
                setGroupIds([]);
              }}
              className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            >
              {myOrgs.map((o) => (
                <option key={o.orgId} value={o.orgId}>{o.orgName}</option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              The meeting belongs to this organisation, and everyone on it
              comes from there. Changing it clears who you have picked.
            </span>
          </label>
        )}

        <AttendeePicker
          selected={attendeeIds}
          onChange={setAttendeeIds}
          disabled={saving}
          people={orgOptions?.people ?? []}
          groups={orgOptions?.groups ?? []}
          loading={optionsLoading}
          onGroupApplied={(id) => setGroupIds((cur) => (cur.includes(id) ? cur : [...cur, id]))}
        />

        {/* External guests - Admin and Super Admin only, because the
            Stakeholders module they come from is. They are listed apart
            from attendees so nobody expects an RSVP from a donor. */}
        {orgOptions?.canPickGuests && (
          <div>
            <span className="block text-sm font-medium text-slate-700">
              External guests
            </span>
            <p className="mt-0.5 text-xs text-slate-400">
              From Stakeholders. They are not invited or notified - they have
              no portal account - and never appear in the clash check.
            </p>
            {guests.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5">
                {guests.map((g) => (
                  <li key={g.stakeholderId}>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700">
                      {g.displayName}
                      {g.primaryTag && (
                        <span className="text-slate-400">
                          · {STAKEHOLDER_TAG_LABEL[g.primaryTag]}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => setGuests((c) =>
                          c.filter((x) => x.stakeholderId !== g.stakeholderId))}
                        aria-label={`Remove ${g.displayName}`}
                        className="text-slate-400 transition hover:text-red-600"
                      >
                        ×
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {/* `canPickGuests` is the caller's role IN THE CHOSEN ORG, not
                the one they are signed into - an Admin of one organisation
                is an ordinary member of another. */}
            <StakeholderSelect
              enabled={orgOptions?.canPickGuests ?? false}
              options={orgOptions?.guests ?? []}
              exclude={guests.map((g) => g.stakeholderId)}
              onPick={(o) => setGuests((c) => [
                ...c,
                { stakeholderId: o.id, displayName: o.displayName, primaryTag: o.primaryTag },
              ])}
            />
          </div>
        )}

        {/* A warning, not a wall: overlapping is sometimes the intention. */}
        {conflicts.length > 0 && (
          <div className="rounded-lg bg-amber-50 px-3 py-2.5 text-xs text-amber-900 ring-1 ring-amber-200">
            <p className="font-semibold">
              Already booked at this time - you can still save.
            </p>
            <ul className="mt-1.5 space-y-1">
              {conflicts.map((c) => (
                <li key={c.userId}>
                  <span className="font-medium">
                    {c.isOrganiser ? 'You' : c.displayName}
                  </span>
                  {': '}
                  {c.events.map((e) => (
                    `${e.title} (${new Date(e.startsAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
                    + `-${new Date(e.endsAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })})`
                  )).join(', ')}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <label htmlFor="event-description" className="block text-sm font-medium text-slate-700">
            Description <span className="ml-1 text-slate-400">(optional)</span>
          </label>
          <textarea
            id="event-description"
            value={description}
            maxLength={4000}
            rows={4}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Agenda, notes, links..."
            className="mt-1.5 w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4">
        {initial ? (
          confirmDelete ? (
            <span className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">Delete this event?</span>
              <button type="button" disabled={saving} onClick={() => void handleDelete()}
                className="rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                Delete
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)}
                className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100">
                Keep
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)}
              className="text-sm font-medium text-red-600 hover:text-red-700">
              Delete event
            </button>
          )
        ) : <span />}

        <span className="flex gap-2">
          <button type="button" onClick={onCancel}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
            {saving ? 'Saving...' : initial ? 'Save changes' : 'Create Meetings'}
          </button>
        </span>
      </div>
    </form>
  );
}
